import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import moment from "moment";
import { FGCustomPanel, FGListen, FGSelectInput, FGTextInput, useDebouncedCallback } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { DogApi, ERequestState, PersonApi, TempRSHRequestApi } from "../../../../api";
import { FGPeopleSearchInput, FGSRSHDateMaskInput } from "../../../../components";
import { useAuth } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalTempRshFormProps {
  tempRshRequestId: number;
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
  showStepper?: boolean;
}

export const DrupalTempRshForm: React.FunctionComponent<IDrupalTempRshFormProps> = ({
  onStepChange,
  tempRshRequestId,
  stepNumber,
  save,
  loading,
  initialdata,
  showStepper = true,
}) => {
  const { user } = useAuth();
  const { t } = useTl();
  const personApi = useApiService(PersonApi);

  const [currentStep, setCurrentStep] = React.useState(stepNumber);
  const [temporaryOwnerIds, setTemporaryOwnerIds] = React.useState<number[]>([]);
  const [tempOwnersLoading, setTempOwnersLoading] = React.useState(false);
  const [temporaryOwnerId, settemporaryOwnerId] = React.useState(null);

  const searchPersonByEmail = React.useCallback(
    async (email: string, formik: any) => {
      formik.setFieldValue("temporaryOwnerId", null);
      settemporaryOwnerId(null);
      setTempOwnersLoading(true);
      const temp = await personApi.getPersonByEmail(email);
      console.log("Temp", temp);
      setTemporaryOwnerIds(temp.map((e) => e.personId));
      if (temp.length === 1) {
        formik.setFieldValue("temporaryOwnerId", temp[0].personId);
        formik.setFieldTouched("temporaryOwnerId", true);
        settemporaryOwnerId(temp[0].personId);
      }
      setTempOwnersLoading(false);
    },
    [personApi]
  );

  const getDebouncedOwnerId = useDebouncedCallback(searchPersonByEmail, 1200);

  const saveForm = React.useCallback(
    async (formikData) => {
      if (currentStep === 1) {
        await save(formikData);
      }
    },
    [currentStep, save]
  );

  const [dogs, dloading] = useReferential(
    (a) => a.getOwnedDogs(user.userId, initialdata?.dogId ? initialdata.dogId : 0, null, null, null),
    true,
    []
  );

  const api = useApiService(TempRSHRequestApi);
  const dogApi = useApiService(DogApi);
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, tempRshRequestId);
          return res.value;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (tempRshRequestId <= 0) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (tempRshRequestId <= 0) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      temporaryOwnerId: Yup.number().nullable().required(t(ETLCodes.Required)),
      temporaryOwnerEmail: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("too-many-results", t(ETLCodes.MoreThanOnePersonAssociatedWithEmail), async function (value) {
          return value && (temporaryOwnerIds.length === 1 || temporaryOwnerIds.length === 0);
        })
        .test("no-results", t(ETLCodes.NoPersonAssociatedWithEmail), async function (value) {
          return value && (temporaryOwnerIds.length === 1 || temporaryOwnerIds.length > 1);
        })
        .test("foreign-owner", t(ETLCodes.OwnerIsNotBelgian), async function (value) {
          if (value && temporaryOwnerId) {
            var isOwner = await personApi.isPersonBelgian(temporaryOwnerId);
            return isOwner.value;
          }
          return true;
        }),
      temporaryOwnerFrom: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("date-correct", t(ETLCodes.DateBeforeRequestDate), function (value) {
          if (this.parent.temporaryOwnerTo) {
            const diff = moment(value).diff(moment(this.parent.request.requestDate), "days");
            return diff >= 0;
          }
          return true;
        }),
      temporaryOwnerTo: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("date-correct", t(ETLCodes.DateTooSmall), function (value) {
          return value > this.parent.temporaryOwnerFrom;
        })
        .test("date-correct", t(ETLCodes.ExceedsAllowedTime), function (value) {
          const oneYearFromNow = new Date(this.parent.temporaryOwnerFrom);
          oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
          return value <= oneYearFromNow;
        }),
    });
  }, [api, dogApi, personApi, t, tempRshRequestId, temporaryOwnerId, temporaryOwnerIds.length]);

  return (
    <DrupalFormGenerator
      initialValues={initialdata}
      editMode={currentStep === 0 && initialdata.request.status === ERequestState.REQUESTED}
      onSubmit={() => {}}
      saving={false}
      validationSchema={RequestedFormSchema}
      loading={loading}
      hideButtons
    >
      <FGListen field="temporaryOwnerEmail" onChanged={(value, formik) => getDebouncedOwnerId(value, formik)} />
      {currentStep !== 0 ? <DrupalFieldSet title={t(ETLCodes.RequestResume)} /> : <></>}
      <FGSelectInput name="dogId" label={t(ETLCodes.Dog)} items={dogs} loading={dloading} />
      <FGTextInput
        label={t(ETLCodes.TemporaryOwner)}
        name="temporaryOwnerEmail"
        requiredMark
        rightElement={
          <Button
            icon={temporaryOwnerId === null ? IconNames.SEARCH : IconNames.TICK_CIRCLE}
            minimal={false}
            disabled
            intent={temporaryOwnerId === null ? Intent.WARNING : Intent.SUCCESS}
            text={temporaryOwnerId === null ? t(ETLCodes.SearchOwners) : t(ETLCodes.OwnerFound)}
            loading={tempOwnersLoading}
          />
        }
      />
      {/* <FGPeopleSearchInput
        name="temporaryOwnerId"
        label={t(ETLCodes.TemporaryOwner)}
        visible={false}
      /> */}
      <FGSRSHDateMaskInput name="temporaryOwnerFrom" label={t(ETLCodes.TemporaryOwnerFrom)} requiredMark />
      <FGSRSHDateMaskInput name="temporaryOwnerTo" label={t(ETLCodes.TemporaryOwnerTo)} requiredMark />
      {showStepper ? (
        <FGCustomPanel>
          {(ctx) => (
            <DrupalStepperButtonBloc
              backOnly={initialdata?.request?.status !== ERequestState.REQUESTED}
              disabled={ctx.formik.values === initialdata || !ctx.formik.isValid}
              loading={loading}
              onStepChange={(newStep) => {
                setCurrentStep(newStep);
                onStepChange(newStep);
              }}
              stepNumber={currentStep}
              onNextFn={() => saveForm(ctx.formik.values)}
              formik={ctx.formik}
            />
          )}
        </FGCustomPanel>
      ) : null}
    </DrupalFormGenerator>
  );
};
